/**
 * AdminRegionTriage — unassigned-venue triage (BON-153), from the design's
 * region-triage.jsx. Active venues with no region, each with a best-effort
 * suggested region: accept it, pick another, or (missing coords) geocode in
 * venue settings. Route /admin/regions/triage.
 */
import { useMemo, useState } from 'react';

import type { RegionNode } from '../../../../api/regions';
import { KpiStrip, type KpiCell } from '../../primitives';
import { BucketError, BucketLoading, SectionCard } from '../../regional/BucketStates';

import { useUnassignedVenues, useRegionTree, useSetVenueException } from './regionsQueries';
import { TriageRow } from './TriageRow';

function regionOptions(regions: RegionNode[]): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  const walk = (n: RegionNode, depth: number) => {
    out.push({ id: n.id, label: `${' '.repeat(depth)}${n.name}` });
    n.children.forEach((c) => walk(c, depth + 1));
  };
  regions.forEach((r) => walk(r, 0));
  return out;
}

function triageKpis(
  inQueue: number,
  suggestions: number,
  needCoords: number,
  accepted: number,
): KpiCell[] {
  return [
    { label: 'In queue', value: String(inQueue) },
    { label: 'Have a suggestion', value: String(suggestions) },
    { label: 'Need coordinates', value: String(needCoords), good: needCoords === 0 },
    { label: 'Accepted this session', value: String(accepted), good: accepted > 0 },
  ];
}

export default function AdminRegionTriage() {
  const queueQ = useUnassignedVenues();
  const treeQ = useRegionTree();
  const accept = useSetVenueException();
  const [acceptedThisSession, setAccepted] = useState(0);

  const venues = useMemo(() => queueQ.data ?? [], [queueQ.data]);
  const options = useMemo(() => regionOptions(treeQ.data ?? []), [treeQ.data]);

  if (queueQ.isLoading) return <BucketLoading />;
  if (queueQ.isError) return <BucketError error={queueQ.error} />;

  const withSuggestion = venues.filter((v) => v.suggestedRegionId && !v.needsCoordinates);
  const needCoords = venues.filter((v) => v.needsCoordinates).length;

  const acceptOne = async (venueId: string, regionId: string) => {
    await accept.mutateAsync({ venueId, mode: 'pin', regionId });
    setAccepted((n) => n + 1);
  };
  const acceptAll = async () => {
    for (const v of withSuggestion) {
      await accept.mutateAsync({
        venueId: v.id,
        mode: 'pin',
        regionId: v.suggestedRegionId as string,
      });
    }
    setAccepted((n) => n + withSuggestion.length);
  };

  const kpis = triageKpis(venues.length, withSuggestion.length, needCoords, acceptedThisSession);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <KpiStrip items={kpis} />

      <SectionCard
        title={`Triage queue · ${venues.length} waiting`}
        action={
          <button
            className="nb-btn nb-btn-primary"
            disabled={accept.isPending || withSuggestion.length === 0}
            onClick={() => void acceptAll()}
            style={{ fontSize: 11.5 }}
          >
            Accept all suggestions{withSuggestion.length ? ` · ${withSuggestion.length}` : ''}
          </button>
        }
      >
        {venues.length === 0 ? (
          <div
            style={{
              padding: '24px 0',
              textAlign: 'center',
              fontSize: 13,
              color: 'var(--nb-subtle)',
            }}
          >
            Queue clear — every venue has a region.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {venues.map((v) => (
              <TriageRow
                key={v.id}
                venue={v}
                regionOptions={options}
                pending={accept.isPending}
                onAccept={(regionId) => void acceptOne(v.id, regionId)}
              />
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
